"use client";

import {
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { useCallback, useRef, useState } from "react";

import { cn } from "@/lib/utils";

import { EngineHeading, EnginePanel } from "./underwriting-engine-chrome";
import { UnderwritingEngineLeft } from "./underwriting-engine-left";
import { UnderwritingEngineMobile } from "./underwriting-engine-mobile";
import { UnderwritingEngineVisual } from "./underwriting-engine-visual";
import type { EngineCardMotion } from "./underwriting-engine-visual";

export interface UnderwritingStep {
  number: "01" | "02" | "03";
  eyebrow: string;
  title: string;
}

export interface UnderwritingEngineProps {
  heading: string;
  subheading: string;
  /** Exactly three steps, ordered 01 → 02 → 03. */
  steps: UnderwritingStep[];
  className?: string;
}

const SPRING = { stiffness: 90, damping: 28, mass: 0.4 } as const;

/** Matches HomeRailSection scroll-mt — pins below fixed site + chapter nav. */
const STICKY_BELOW_CHAPTER_NAV =
  "top-[calc(var(--nav-offset)+var(--chapter-nav-h)+16px)]" as const;

export function UnderwritingEngine({
  heading,
  subheading,
  steps,
  className,
}: UnderwritingEngineProps) {
  const reduceMotion = useReducedMotion();

  return (
    <div className={cn("flex flex-col gap-8", className)}>
      {reduceMotion ? (
        <StaticEngine heading={heading} subheading={subheading} steps={steps} />
      ) : (
        <>
          <UnderwritingEngineMobile
            className="lg:hidden"
            heading={heading}
            subheading={subheading}
            steps={steps}
          />
          <AnimatedEngine
            className="hidden lg:block"
            heading={heading}
            subheading={subheading}
            steps={steps}
          />
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Desktop: scroll-pinned, layers drop in from the top one by one.     */
/* ------------------------------------------------------------------ */

/**
 * Scroll progress at which each step's colour layer has finished landing —
 * and, by construction, the instant before the next glass sheet starts to
 * drop (each sheet's window opens where the layer below it closes).
 *
 * This is the state a step promises: "layer N is down, nothing on top of it
 * yet". Both the step indicator and the click targets read from here, so the
 * label always names the layer actually on screen. Keep in sync with the
 * layer windows in `cards` / `glassCards` below.
 */
export const STEP_LANDINGS = [0, 0.39, 0.65] as const;

/** Index of the last step whose layer has landed at progress `p`. */
export function stepAtProgress(p: number, stepCount: number): number {
  if (STEP_LANDINGS.length !== stepCount) {
    // Layer windows are authored for three steps; fall back to even slices.
    return Math.min(stepCount - 1, Math.max(0, Math.floor(p * stepCount)));
  }
  let index = 0;
  STEP_LANDINGS.forEach((landing, i) => {
    if (p >= landing) index = i;
  });
  return index;
}

function AnimatedEngine({
  heading,
  subheading,
  steps,
  className,
}: UnderwritingEngineProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeStep, setActiveStep] = useState(0);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const next = stepAtProgress(p, steps.length);
    setActiveStep((cur) => (cur === next ? cur : next));
  });

  // Layers slide in from above the section (overflowing the top) and stack
  // in an alternating sequence: colour layer, THEN its glass sheet, then the
  // next colour layer, … and finally the glass-box walls close the cube.
  // No fade — pure translate. The large start offset keeps every layer
  // clipped above the panel until its turn.
  const DROP = -620;
  const cards: EngineCardMotion[] = [
    {
      // Layer 01 is already down when the section is reached: the panel reads
      // as a started engine rather than an empty box, and the scroll drives
      // what stacks on top of it. Held at rest, so no spring to settle.
      y: useMotionValue(0),
    },
    {
      y: useSpring(
        useTransform(scrollYProgress, [0.26, 0.39], [DROP, 0]),
        SPRING
      ),
    },
    {
      y: useSpring(
        useTransform(scrollYProgress, [0.52, 0.65], [DROP, 0]),
        SPRING
      ),
    },
  ];
  const glassCards: EngineCardMotion[] = [
    {
      y: useSpring(
        useTransform(scrollYProgress, [0.13, 0.26], [DROP, 0]),
        SPRING
      ),
    },
    {
      y: useSpring(
        useTransform(scrollYProgress, [0.39, 0.52], [DROP, 0]),
        SPRING
      ),
    },
    {
      y: useSpring(
        useTransform(scrollYProgress, [0.65, 0.78], [DROP, 0]),
        SPRING
      ),
    },
  ];
  // The box is the finale — the glass cube fades in FAST, and only once the
  // lid (last glass sheet, window ending 0.78) has landed AND its spring has
  // settled — hence the 0.87 start. Painted + self-blurred interior, so a
  // plain opacity fade is safe (no backdrop-filter to keep sampling).
  const box = {
    veil: useSpring(
      useTransform(scrollYProgress, [0.87, 0.89], [0, 1]),
      // Snappier than the drop spring — the cube should pop in, not drift.
      { stiffness: 220, damping: 30, mass: 0.3 }
    ),
  };

  // Smoothed scroll progress driving the waveform playhead.
  const progress = useSpring(scrollYProgress, SPRING);

  const scrollToStep = useCallback(
    (stepIndex: number) => {
      const section = sectionRef.current;
      if (!section) return;

      const sectionTop = section.getBoundingClientRect().top + window.scrollY;
      const scrollRange = section.offsetHeight - window.innerHeight;
      if (scrollRange <= 0) return;

      // Land exactly where this step's layer finished dropping. The old
      // mid-slice target ((index + 0.5) / count) sat partway into the *next*
      // sheet's window, so clicking 02 arrived with the glass above it 85%
      // down — a layer the step never mentions.
      const clamped = Math.min(steps.length - 1, Math.max(0, stepIndex));
      const targetProgress =
        STEP_LANDINGS.length === steps.length
          ? STEP_LANDINGS[clamped]!
          : (clamped + 0.5) / steps.length;

      window.scrollTo({
        top: sectionTop + targetProgress * scrollRange,
        behavior: "smooth",
      });
    },
    [steps.length]
  );

  return (
    <section
      ref={sectionRef}
      className={cn("relative", className)}
      style={{ height: `${steps.length * 100}vh` }}
    >
      <div
        className={cn("sticky flex flex-col gap-8", STICKY_BELOW_CHAPTER_NAV)}
      >
        <EngineHeading heading={heading} subheading={subheading} />
        <EnginePanel className="h-[560px]">
          <UnderwritingEngineLeft
            steps={steps}
            activeStep={activeStep}
            progress={progress}
            onStepSelect={scrollToStep}
            className="absolute top-1/2 left-[7%] w-[42%] max-w-[465px] -translate-y-1/2"
          />
          <UnderwritingEngineVisual
            steps={steps}
            activeStep={activeStep}
            cards={cards}
            glassCards={glassCards}
            box={box}
            onStepSelect={scrollToStep}
            className="absolute top-1/2 right-[6%] -translate-y-1/2"
          />
          <ScreenReaderProgress activeStep={activeStep} steps={steps} />
        </EnginePanel>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/* Static: mobile + reduced-motion. No pin, every step visible.        */
/* ------------------------------------------------------------------ */

function StaticEngine({
  heading,
  subheading,
  steps,
  className,
}: UnderwritingEngineProps) {
  return (
    <div className={cn("flex flex-col gap-8", className)}>
      <EngineHeading heading={heading} subheading={subheading} />
      <EnginePanel className="flex flex-col items-center gap-12 px-6 py-10 md:px-10 md:py-14">
        <UnderwritingEngineLeft
          steps={steps}
          activeStep={0}
          showAll
          className="w-full max-w-[520px]"
        />
        <UnderwritingEngineVisual
          steps={steps}
          activeStep={steps.length - 1}
          className="hidden shrink-0 sm:block"
        />
      </EnginePanel>
    </div>
  );
}

function ScreenReaderProgress({
  activeStep,
  steps,
}: {
  activeStep: number;
  steps: UnderwritingStep[];
}) {
  return (
    <p className="sr-only" aria-live="polite">
      Step {activeStep + 1} of {steps.length}: {steps[activeStep]?.eyebrow}
    </p>
  );
}
